<template>
  <div
    v-touch.long="() => emit('long-press')"
    class="group relative flex cursor-pointer items-start gap-2 rounded-8 px-2 py-1.5 hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres"
    :style="anchorStyle"
    data-test="chat-gallery-video-item"
    @click="emit('open')"
    @contextmenu.prevent="emit('contextmenu')"
  >
    <div class="relative size-[72px] shrink-0 overflow-hidden rounded-4 bg-surf-neut-soft-def">
      <video
        v-if="previewSrc"
        :src="previewSrc"
        class="pointer-events-none h-full w-full object-cover"
        muted
        playsinline
        preload="metadata"
        @loadedmetadata="onVideoMetadata"
      />
      <span
        v-if="durationLabel"
        class="absolute bottom-1 right-1 rounded-4 bg-black/70 px-1 text-label-r-11-16 text-white"
        data-test="chat-gallery-video-duration"
      >
        {{ durationLabel }}
      </span>
    </div>

    <div class="min-w-0 flex-1 py-0.5">
      <p
        class="truncate text-label-m-13-18 text-fg-base-fg3"
        :title="title"
      >
        {{ title }}
      </p>
      <p
        v-if="!isEventRecording && senderName"
        class="truncate text-label-r-12-18 text-fg-base-fg17"
        data-test="chat-gallery-video-sender"
      >
        {{ senderName }}
      </p>
      <div class="flex min-w-0 items-center gap-1">
        <wui-icon
          v-if="isEventRecording"
          name="recordM"
          class="size-4! shrink-0 text-fg-neg-def"
          data-test="chat-gallery-video-recording-icon"
        />
        <p class="truncate text-label-r-12-18 text-fg-base-fg17">
          {{ metaLabel }}
        </p>
      </div>
    </div>

    <button
      v-if="showKebab"
      type="button"
      class="absolute top-1 right-1 z-10 flex size-6 items-center justify-center rounded-4 text-fg-base-fg17 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-surf-neut-soft-hov"
      :class="{ 'opacity-100': isMenuActive }"
      data-test="chat-gallery-video-menu"
      :aria-label="$t('common.more')"
      @click.stop="emit('open-menu')"
    >
      <wui-icon
        name="kebabVerticalM"
        class="size-4!"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { computed, ref } from 'vue'

import { vTouch } from '@/shared/directives/v-touch'

const props = withDefaults(defineProps<{
  title: string
  senderName?: string
  metaLabel: string
  previewSrc?: string
  durationSeconds?: number
  isEventRecording?: boolean
  showKebab?: boolean
  isMenuActive?: boolean
  anchorName?: string
}>(), {
  senderName: '',
  previewSrc: '',
  durationSeconds: 0,
  isEventRecording: false,
  showKebab: false,
  isMenuActive: false,
  anchorName: '',
})

const emit = defineEmits<{
  (event: 'open'): void
  (event: 'open-menu'): void
  (event: 'contextmenu'): void
  (event: 'long-press'): void
}>()

const loadedDurationSeconds = ref<number | undefined>(undefined)

const anchorStyle = computed(() => {
  if (!props.anchorName) {
    return undefined
  }

  return { 'anchor-name': props.anchorName }
})

const formatDuration = (totalSeconds: number) => {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const restSeconds = seconds % 60
  const mm = String(minutes).padStart(hours > 0 ? 2 : 1, '0')
  const ss = String(restSeconds).padStart(2, '0')

  if (hours > 0) {
    return `${hours}:${mm.padStart(2, '0')}:${ss}`
  }

  return `${minutes}:${ss}`
}

const durationLabel = computed(() => {
  const value = props.durationSeconds > 0
    ? props.durationSeconds
    : loadedDurationSeconds.value
  if (value === undefined || !Number.isFinite(value) || value <= 0) {
    return ''
  }

  return formatDuration(value)
})

const onVideoMetadata = (event: Event) => {
  const video = event.target as HTMLVideoElement
  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    return
  }

  loadedDurationSeconds.value = video.duration
  if (video.currentTime === 0) {
    video.currentTime = 0.001
  }
}
</script>
